/**
 * Pure helpers for the published-body preview.
 *
 * DEPENDENCY-FREE ON PURPOSE, like `components/sites/siteSpec.ts`: CRA's jest
 * cannot resolve react-router-dom v7 here, so anything importing a component is
 * untestable. Every decision worth asserting lives in this module and the
 * component only renders what it returns.
 */

import { CitationStyle } from '../../types';

/** The choices the control offers, in the order they escalate. */
export const CITATION_STYLES: { value: CitationStyle; label: string; hint: string }[] = [
  {
    value: 'off',
    label: 'No sources',
    hint: 'The claim stands on its own. What every page publishes today.',
  },
  {
    value: 'footnote',
    label: 'Footnotes',
    hint: 'Each cited claim gets a numbered reference and the page gains a References section.',
  },
  {
    value: 'link',
    label: 'Inline links',
    hint: 'Each cited claim becomes a link to the source it rests on.',
  },
];

export function citationStyleLabel(style: CitationStyle | null | undefined): string {
  return CITATION_STYLES.find((s) => s.value === style)?.label || 'No sources';
}

/**
 * What to tell the reader about this render, or null when there is nothing
 * worth saying.
 *
 * THREE STATES, and conflating them is the failure this exists to prevent. A
 * page that cannot show sources looks identical to one whose site has them
 * switched off, and both look identical to a style you are only trying out —
 * so a silent control would leave you unable to tell "it did nothing" from "it
 * did exactly what you asked".
 */
export function previewNotice(
  args: {
    hasCitations: boolean;
    style: CitationStyle;
    siteStyle: CitationStyle;
    siteType?: string | null;
  },
): { tone: 'info' | 'warn'; text: string } | null {
  const { hasCitations, style, siteStyle, siteType } = args;
  if (!hasCitations) {
    return {
      tone: 'warn',
      text:
        'This version has no tracked sources, so it publishes the same way ' +
        'whichever option is chosen. Pages built before citations were recorded ' +
        'are in this state.',
    };
  }
  if (style !== siteStyle) {
    return {
      tone: 'info',
      text:
        `Trying ${citationStyleLabel(style).toLowerCase()}. This site publishes ` +
        `${citationStyleLabel(siteStyle).toLowerCase()} - change it in the site's ` +
        'settings to make this what deploys.',
    };
  }
  if (!siteType) {
    return {
      tone: 'info',
      text: 'This page is not on a deployment plan yet, so it is showing the default.',
    };
  }
  return null;
}
